import React, { FormEventHandler, useCallback, useEffect, useState } from 'react';

import { Button, Card, CardActions, CardContent, CardHeader, Grid, TextField } from '@mui/material';

import TourOfferCountryInput from '../ToursListing/TourOfferCountryInput';
import TourOfferStatusInput from '../ToursListing/TourOfferStatusInput';

import TourOfferSellingRegionInput from './TourOfferSellingRegionInput';
import TourOfferTTCBrandInput from './TourOfferTTCBrandInput';

export interface TTCTourSearchInput {
  vendors?: string[];
  countries?: string[];
  sellingRegion?: string;
  status?: '' | 'content-approved' | 'draft';
  tourName?: string;
}

const DEFAULT_TTC_TOUR_SEARCH_INPUT: TTCTourSearchInput = {
  vendors: [],
  countries: [],
  sellingRegion: 'any',
  status: '',
  tourName: '',
};

interface Props {
  initialInput?: TTCTourSearchInput;
  onSubmit: (formObject: Record<string, string>) => void;
}

function TTCTourSearchForm(props: Props) {
  const { initialInput, onSubmit } = props;

  const [tourName, setTourName] = useState<string>(DEFAULT_TTC_TOUR_SEARCH_INPUT.tourName);
  const [status, setStatus] = useState<string>(DEFAULT_TTC_TOUR_SEARCH_INPUT.status);
  const [sellingRegion, setSellingRegion] = useState<string>(DEFAULT_TTC_TOUR_SEARCH_INPUT.sellingRegion);
  const [vendors, setVendors] = useState<string[]>(DEFAULT_TTC_TOUR_SEARCH_INPUT.vendors);
  const [countries, setCountries] = useState<string[]>(DEFAULT_TTC_TOUR_SEARCH_INPUT.countries);

  const handleSubmission = useCallback<FormEventHandler<HTMLFormElement>>(
    (e) => {
      e.preventDefault();

      const formObject: Record<string, string> = {};

      if (tourName) formObject.tourName = tourName;
      if (status) formObject.status = status;
      if (sellingRegion && sellingRegion !== 'any') formObject.sellingRegion = sellingRegion;
      if (vendors?.length) formObject.vendors = vendors.join(',');
      if (countries?.length) formObject.countries = countries.join(',');

      onSubmit(formObject);
    },
    [vendors, countries, onSubmit, sellingRegion, status, tourName],
  );

  const handleReset = useCallback<FormEventHandler<HTMLFormElement>>((e) => {
    e.preventDefault();

    setVendors(DEFAULT_TTC_TOUR_SEARCH_INPUT.vendors);
    setCountries(DEFAULT_TTC_TOUR_SEARCH_INPUT.countries);
    setSellingRegion(DEFAULT_TTC_TOUR_SEARCH_INPUT.sellingRegion);
    setStatus(DEFAULT_TTC_TOUR_SEARCH_INPUT.status);
    setTourName(DEFAULT_TTC_TOUR_SEARCH_INPUT.tourName);
  }, []);

  useEffect(() => {
    setVendors(initialInput?.vendors ?? DEFAULT_TTC_TOUR_SEARCH_INPUT.vendors);
    setCountries(initialInput?.countries ?? DEFAULT_TTC_TOUR_SEARCH_INPUT.countries);
    setSellingRegion(initialInput?.sellingRegion ?? DEFAULT_TTC_TOUR_SEARCH_INPUT.sellingRegion);
    setTourName(initialInput?.tourName ?? DEFAULT_TTC_TOUR_SEARCH_INPUT.tourName);
    setStatus(initialInput?.status ?? DEFAULT_TTC_TOUR_SEARCH_INPUT.status);
  }, [initialInput]);

  return (
    <form onSubmit={handleSubmission} onReset={handleReset}>
      <Card>
        <CardHeader title="Search for TTC Tours" />
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12}>
              <TextField label="Tour name" value={tourName} onChange={(e) => setTourName(e.target.value)} fullWidth />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TourOfferStatusInput value={status} onChange={setStatus} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TourOfferSellingRegionInput value={sellingRegion} onChange={setSellingRegion} />
            </Grid>
            <Grid item xs={12} sm={12}>
              <TourOfferTTCBrandInput value={vendors} onChange={setVendors} />
            </Grid>
            <Grid item xs={12}>
              <TourOfferCountryInput value={countries} onChange={setCountries} />
            </Grid>
          </Grid>
        </CardContent>
        <CardActions sx={{ justifyContent: 'end' }}>
          <Button variant="text" size="large" type="reset">
            Reset
          </Button>
          <Button variant="contained" size="large" type="submit">
            Search
          </Button>
        </CardActions>
      </Card>
    </form>
  );
}

export default TTCTourSearchForm;
